// useWindParticleDebug — dev-only hook. When the URL has `?windDebug=1`,
// mount the WindParticleLayer on the map, fetch the wind PNG via
// /api/wind-raster, and wire keyboard shortcuts to swap altitude/forecast
// hour for testing.
//
// Removed in commit 3 when the real controller + UI ship.
//
// URL params (all optional):
//   ?windDebug=1                 — turn the layer on
//   &alt=10000                   — initial altitude (ft MSL), default 10000
//   &hour=0                      — initial forecast hour, default 0
//   &particles=60000             — particle count, default 60000
//   &speed=0.25                  — speed factor, default 0.25
//   &fade=0.96                   — trail fade, default 0.96
//
// Keyboard shortcuts (while focused on map):
//   [ / ]                        — step altitude down/up
//   , / .                        — step forecast hour back/forward
//
// Logs to `[WindDebug]` prefix so we can grep them out.

import { useEffect } from 'react';
import { getMapInstance } from '../MapView';
import { WindParticleLayer } from './windParticleLayer';

const ALT_STEPS = [5000, 8000, 10000, 12000, 18000, 24000, 30000, 34000];
const HOUR_STEPS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

interface State {
  layer: WindParticleLayer;
  alt: number;
  hour: number;
  loadVersion: number;
}

async function loadPng(alt: number, hour: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`PNG load failed: ${e}`));
    img.src = `/api/wind-raster?alt=${alt}&hour=${hour}`;
  });
}

async function applyAltHour(state: State, alt: number, hour: number) {
  state.alt = alt;
  state.hour = hour;
  const version = ++state.loadVersion;
  // eslint-disable-next-line no-console
  console.log(`[WindDebug] loading alt=${alt}ft hour=${hour}`);
  try {
    const img = await loadPng(alt, hour);
    if (version !== state.loadVersion) return; // newer load won
    state.layer.setWindImage(img);
    // eslint-disable-next-line no-console
    console.log(`[WindDebug] applied alt=${alt}ft hour=${hour} (${img.width}×${img.height})`);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`[WindDebug] alt=${alt} hour=${hour} failed:`, e);
  }
}

export function useWindParticleDebug() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('windDebug') !== '1') return;

    const opts = {
      particleCount: Number(params.get('particles')) || 60000,
      speedFactor: Number(params.get('speed')) || 0.25,
      fadeOpacity: Number(params.get('fade')) || 0.96,
    };
    const initialAlt = Number(params.get('alt')) || 10000;
    const initialHour = Number(params.get('hour')) || 0;

    let cancelled = false;
    let installed = false;
    let state: State | null = null;

    function tryInstall() {
      const map = getMapInstance();
      if (!map) {
        // Try again on next frame
        if (!cancelled) requestAnimationFrame(tryInstall);
        return;
      }
      if (installed) return;
      installed = true;
      const layer = new WindParticleLayer(opts);
      map.addLayer(layer as unknown as maplibregl.LayerSpecification);
      state = { layer, alt: initialAlt, hour: initialHour, loadVersion: 0 };
      // eslint-disable-next-line no-console
      console.log(
        `[WindDebug] layer added (particles=${layer.particleCount}, speed=${layer.speedFactor}, trails=geo-lines)`,
      );
      void applyAltHour(state, initialAlt, initialHour);
    }
    tryInstall();

    function onKey(e: KeyboardEvent) {
      if (!state) return;
      const altIdx = ALT_STEPS.indexOf(state.alt);
      const hrIdx = HOUR_STEPS.indexOf(state.hour);
      let nextAlt = state.alt;
      let nextHour = state.hour;
      if (e.key === ']' && altIdx >= 0 && altIdx < ALT_STEPS.length - 1) {
        nextAlt = ALT_STEPS[altIdx + 1];
      } else if (e.key === '[' && altIdx > 0) {
        nextAlt = ALT_STEPS[altIdx - 1];
      } else if (e.key === '.' && hrIdx >= 0 && hrIdx < HOUR_STEPS.length - 1) {
        nextHour = HOUR_STEPS[hrIdx + 1];
      } else if (e.key === ',' && hrIdx > 0) {
        nextHour = HOUR_STEPS[hrIdx - 1];
      } else {
        return;
      }
      if (nextAlt !== state.alt || nextHour !== state.hour) {
        void applyAltHour(state, nextAlt, nextHour);
      }
    }
    window.addEventListener('keydown', onKey);

    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKey);
      if (installed) {
        const map = getMapInstance();
        if (map) {
          try {
            map.removeLayer('wind-particles');
          } catch {
            /* layer wasn't there */
          }
        }
      }
    };
  }, []);
}
